import React, { useEffect, useMemo, useState } from "react";
import { Download, TrendingUp, Coins, Undo2, Wallet } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatINR, downloadBlob } from "@/lib/api";
import { toast } from "sonner";

const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))"];

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

function formatAxisValue(v) {
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}k`;
  return `₹${v}`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-border bg-[hsl(var(--popover))] px-3 py-2 shadow-[0_1px_0_rgba(27,20,22,0.06),0_10px_30px_rgba(27,20,22,0.06)] text-xs">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={p.dataKey || i} className="text-muted-foreground">{p.name}: <span className="font-mono text-foreground">{formatINR(p.value)}</span></div>
      ))}
    </div>
  );
}

// Supplier-wise Purchase Register — totals, returns and net-payable trend per supplier,
// scoped by an optional supplier filter. Complements the aggregate Analytics tab with a
// drill-down view Accounts/Purchase teams need for supplier reconciliation.
export function SupplierRegisterTab() {
  const [suppliersList, setSuppliersList] = useState([]);
  const [supplierId, setSupplierId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/purchase/suppliers").then(({ data }) => setSuppliersList(data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { months: 12 };
      if (supplierId !== "all") params.supplier_id = supplierId;
      const { data: d } = await api.get("/purchase/reports/supplier-register", { params });
      setData(d);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [supplierId]);

  const trend = (data?.monthly_trend || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  const rows = useMemo(() => {
    const all = data?.suppliers || [];
    return supplierId === "all" ? all : all.filter((r) => r.supplier_id === supplierId);
  }, [data, supplierId]);
  const totals = data?.totals || { total_purchases: 0, total_paid: 0, total_returns: 0, net_payable: 0 };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = supplierId !== "all" ? `?supplier_id=${encodeURIComponent(supplierId)}` : "";
      await downloadBlob(`/purchase/reports/supplier-register.csv${params}`, "Supplier-Purchase-Register.csv");
      toast.success("Register exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="supplier-register-tab">
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium">Supplier Purchase Register</div>
          <p className="text-xs text-muted-foreground">Totals, returns and net-payable trend, per supplier.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger data-testid="supplier-register-filter" className="w-[200px]"><SelectValue placeholder="All Suppliers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {suppliersList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="supplier-register-export-button" variant="outline" size="sm" className="gap-1" onClick={exportCsv} disabled={exporting}>
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="register-kpi-total-purchases">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Purchases</span><Coins className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(totals.total_purchases)}</div>
        </Card>
        <Card className="p-4" data-testid="register-kpi-total-paid">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Paid</span><Wallet className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(totals.total_paid)}</div>
        </Card>
        <Card className="p-4" data-testid="register-kpi-total-returns">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Returns</span><Undo2 className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(totals.total_returns)}</div>
        </Card>
        <Card className="p-4" data-testid="register-kpi-net-payable">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Net Payable</span><TrendingUp className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(totals.net_payable)}</div>
        </Card>
      </div>

      {error ? (
        <Card className="p-8 text-center" data-testid="supplier-register-error-state">
          <p className="text-sm text-muted-foreground mb-3">Could not load the purchase register right now.</p>
          <button data-testid="supplier-register-retry-button" onClick={load} className="text-sm text-[hsl(var(--primary))] underline underline-offset-2">Retry</button>
        </Card>
      ) : (
        <Card className="p-4" data-testid="register-payable-trend-chart">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Payable Trend (12 months){supplierId !== "all" ? " — " + (suppliersList.find((s) => s.id === supplierId)?.name || "") : ""}</span></div>
          {loading ? <Skeleton className="h-64 w-full" /> : trend.every((t) => t.purchases === 0 && t.returns === 0) ? (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No purchase history yet — record a purchase to see trends here.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} tickFormatter={formatAxisValue} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="purchases" name="Purchases" stroke={CHART_COLORS[0]} strokeWidth={2.5} dot={{ r: 3, fill: CHART_COLORS[0] }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="returns" name="Returns" stroke={CHART_COLORS[1]} strokeWidth={2.5} dot={{ r: 3, fill: CHART_COLORS[1] }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="net_payable" name="Net Payable" stroke={CHART_COLORS[2]} strokeWidth={2.5} dot={{ r: 3, fill: CHART_COLORS[2] }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      <Card className="p-4">
        <div className="text-sm font-medium mb-3">Supplier-wise Register</div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table data-testid="supplier-register-table">
            <TableHeader>
              <TableRow className="bg-[hsl(var(--muted))]">
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Returns</TableHead>
                <TableHead className="text-right">Net Payable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="supplier-register-empty-state">No supplier purchase history yet — record a purchase to see the register here.</TableCell></TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.supplier_id} data-testid={`supplier-register-row-${r.supplier_id}`}>
                    <TableCell className="font-medium">{r.supplier_name}</TableCell>
                    <TableCell className="text-right font-mono">{r.purchase_count}</TableCell>
                    <TableCell className="text-right font-mono">{formatINR(r.total_purchases)}</TableCell>
                    <TableCell className="text-right font-mono">{formatINR(r.total_paid)}</TableCell>
                    <TableCell className="text-right font-mono">{formatINR(r.total_returns)}</TableCell>
                    <TableCell className={`text-right font-mono ${r.net_payable > 0 ? "text-destructive" : ""}`}>{formatINR(r.net_payable)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
